import * as React from 'react';
import { alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AppNavbar from './AppNavbar';
import Header from './Header';
import SideMenu from './SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from '../pages/theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

export function AdminSection({ title, subtitle, children, action = null }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box>
            <Typography variant="h6">{title}</Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          {action}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

export function AdminStatCard({ label, value, detail }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        minHeight: 140,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background: (theme) =>
          `linear-gradient(180deg, ${alpha(theme.palette.primary.light, 0.14)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
      }}
    >
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1, fontWeight: 700 }}>
        {value}
      </Typography>
      {detail ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {detail}
        </Typography>
      ) : null}
    </Paper>
  );
}

export default function AdminLayout({ children, breadcrumbItems, title, subtitle, props }) {
  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.vars
              ? `rgba(${theme.vars.palette.background.defaultChannel} / 1)`
              : alpha(theme.palette.background.default, 1),
            overflow: 'auto',
          })}
        >
          <Stack
            spacing={3}
            sx={{
              alignItems: 'stretch',
              maxWidth: 1600,
              mx: 'auto',
              px: { xs: 2, md: 3 },
              pb: 8,
              mt: { xs: 10, md: 0 },
            }}
          >
            <Header breadcrumbItems={breadcrumbItems} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {title}
              </Typography>
              {subtitle ? (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            {children}
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
